import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthenticationService } from '../../../services/authentication.service';
import { BUTTON_SIZE, BUTTON_TYPES } from '@shared/components/button/button-types.enum';
import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { DELOITTE_STR, EMAIL_INPUT_REGEX, emailFormat, PASTE_EMAIL_REGEX } from '../validators.const';
import { Router } from '@angular/router';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ForgotPasswordModalComponent } from '../forgot-password-modal/forgot-password-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MessageToasterComponent, ToasterType } from '@shared/components/message-toaster/message-toaster.component';
import { take } from 'rxjs';
import { VerificationModalComponent } from '../verification-modal/verification-modal.component';
import { TranslateModule } from '@ngx-translate/core';
import { HelperService } from '../../../services/helper.service';

export interface ILoginForm {
  email: FormControl<string | null>;
  password: FormControl<string | null>;
}

@UntilDestroy()
@Component({
  selector: 'app-login-modal',
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    InputComponent,
    TranslateModule,
  ],
  templateUrl: './login-modal.component.html',
  styleUrl: './login-modal.component.scss',
})
export class LoginModalComponent implements OnInit {
  dialog = inject(MatDialog);
  snackBar = inject(MatSnackBar);

  // First step in form submission, checks if user uses deloitte flow or cognito
  firstLogin = true;

  loginForm!: FormGroup<ILoginForm>;
  firstEmail = new FormControl('', [Validators.required, emailFormat]);

  dialogRef = inject<DialogRef<string>>(DialogRef<string>);
  data = inject(DIALOG_DATA);
  isDeloitteEmail: boolean = false;
  isVerificationEmailSent = false;
  isEmailSubmitted = false;
  isFormSubmitted = false;

  protected readonly PASTE_EMAIL_REGEX = PASTE_EMAIL_REGEX;
  protected readonly EMAIL_INPUT_REGEX = EMAIL_INPUT_REGEX;
  protected readonly ButtonTypes = BUTTON_TYPES;
  protected readonly BUTTON_SIZE = BUTTON_SIZE;

  constructor(
    private authService: AuthenticationService,
    private formBuilder: FormBuilder,
    private router: Router,
    private HELPER: HelperService,
  ) {
  }

  ngOnInit(): void {
    this.createLoginForm();
  }

  createLoginForm(): void {
    this.loginForm = this.formBuilder.group({
      email: new FormControl('', [Validators.required, emailFormat]),
      password: new FormControl('', [Validators.required]),
    });

    this.loginForm.controls.email.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(email => {
        if (email) {
          this.isDeloitteEmail = email.toLowerCase().includes(DELOITTE_STR);
        }
      });
  }

  onForgotPassword(): void {
    this.dialog.open(ForgotPasswordModalComponent, { panelClass: 'custom-dialog' })
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => this.dialog.closeAll());
  }

  onSignIn(): void {
    // show validation errors
    this.isFormSubmitted = true;
    if (this.isDeloitteEmail) {
      this.authService.handleMicrosoftSignIn();
    } else {
      if (this.loginForm.valid) {
        this.handleCognitoSignIn();
      }
    }
  }

  handleCognitoSignIn(): void {
    const email = this.loginForm.value.email?.toString();
    const password = this.loginForm.value.password?.toString();
    if (email && password) {
      this.authService.signIn(email, password)
        .then(session => {


          this.fetchSessionAfterLogIn();
          this.closeModal();
          this.authService.getUser().then(user => {
            const message = `You logged in as ${user.attributes.given_name} ${user.attributes.family_name}`;
            this.openMessage(ToasterType.SUCCESS, message);
          });
        })
        .catch(error => {
          if (error.message === 'User is not confirmed.') {
            this.openVerificationModal();
          }
          this.openMessage(ToasterType.ERROR, error.message);
        });
    }
  }

  fetchSessionAfterLogIn() {
    this.authService.fetchAuthSession().then(session => {
      this.authService.setSession(session);
      this.authService.getUser().then(user => {
        this.authService.user.next(user);
      });
    });
  }

  signUp(): void {
    this.router.navigate([`${this.HELPER.frLoc}/signup`]);
    this.closeModal();
  }

  closeModal() {
    this.dialogRef.close();
  }

  openMessage(type: ToasterType, message: string, title?: string) {
    this.snackBar.openFromComponent(MessageToasterComponent, {
      data: {
        message: message,
        title: title,
      },
      panelClass: type,
      duration: 5000,
    });
  }

  openVerificationModal(): void {
    if (!this.isVerificationEmailSent) {
      this.authService.resendCode(String(this.loginForm.value.email));
      this.isVerificationEmailSent = true;
    }
    const dialogRef = this.dialog.open(VerificationModalComponent, {
      panelClass: 'custom-dialog',
      data: { email: this.loginForm.value.email },
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(code => {
        if (code) {
          this.onSignIn();
        }
      });
  }

  onSubmitEmail($event: any): void {
    // validate inputs, show errors
    this.isEmailSubmitted = true;
    if (this.firstEmail.valid) {
      this.isDeloitteEmail = !!this.firstEmail.value?.toLowerCase().includes(DELOITTE_STR);
      if (this.isDeloitteEmail) {
        this.authService.handleMicrosoftSignIn();
      } else {
        // hide email form and show email + password form
        this.loginForm.controls.email.setValue(this.firstEmail.value);
        this.firstLogin = false;
      }
    }
  }
}
